import { S3Client } from "@aws-sdk/client-s3";
import { nanoid } from "nanoid";
import urlJoin from "url-join";
import config, { isTesting } from "src/config";
import {
  CognitoIdentityClient,
  GetIdCommand,
  GetCredentialsForIdentityCommand,
} from "@aws-sdk/client-cognito-identity";
import { Upload } from "@aws-sdk/lib-storage";
import { AwsCredentialIdentity } from "@smithy/types";

type Credentials = AwsCredentialIdentity & { identityId: string };
function getS3Client(credentials: Credentials) {
  if (isTesting()) {
    return new S3Client({
      endpoint: config.testing?.s3.endpoint,
      region: config.s3.REGION,
      credentials,
      forcePathStyle: true,
    });
  } else {
    return new S3Client({
      region: config.s3.REGION,
      credentials,
      // A recent aws-sdk update introduced checksums for data integrity. This
      // seems to break for us on large uploads. I imagine this is a bug in
      // aws-sdk. Until it's fixed, this will disable the checksum calculation.
      // If you want to test if this is fixed, remove the
      // requestChecksumCalculation line and upload a file larger than 6MB.
      //
      // Suggested in:J
      // https://github.com/aws/aws-sdk-js-v3/issues/6834#issuecomment-2613306914
      //
      // This is the error we were getting:
      //
      // The upload was created using a crc32 checksum. The complete request
      // must include the checksum for each part. It was missing for part 1 in
      // the request.
      requestChecksumCalculation: "WHEN_REQUIRED",
    });
  }
}

let cachedCredentials: Credentials;

// if the access token changes, we need to reload the credentials
let lastAccessToken: string;

export async function s3Upload(
  file: File,
  accessToken: string | null | undefined,
  folder: string | undefined = undefined,
  onProgress?: (progress: number) => void
) {
  const credentials = await getAwsCredentials(accessToken);

  if (!credentials) {
    throw Error("No credentials found.");
  }

  const s3 = getS3Client(credentials);

  const extension = file.name.split(".").pop();
  const username = credentials.identityId;

  const filename = folder
    ? `${folder}/${username}-${nanoid()}-${extension}`
    : `${username}-${nanoid()}-${extension}`;

  // We can't use the PutObjectCommand because the body is a ReadStream and AWS
  // can't get the length of a ReadStream for some reason. You'll get this
  // warning and it'll fail:
  //
  // Are you using a Stream of unknown length as the Body of a PutObject
  // request? Consider using Upload instead from @aws-sdk/lib-storage.
  //
  // Or in the browser you'll get this error:
  //
  // readableStream.getReader is not a function
  const upload = new Upload({
    client: s3,
    params: {
      Bucket: config.s3.UPLOAD_BUCKET,
      Key: filename,
      Body: file,
      ContentType: file.type,
      ACL: "public-read",
    },
  });

  if (onProgress) {
    upload.on("httpUploadProgress", (evt) => {
      if (evt.loaded && evt.total) {
        onProgress((evt.loaded / evt.total) * 100);
      }
    });
  }

  await upload.done();

  const fileUrl = urlJoin(
    `https://${config.s3.UPLOAD_BUCKET}.s3.${config.s3.REGION}.amazonaws.com`,
    filename
  );

  return fileUrl;
}

// To use Cognito to auth for S3, you need to directly set
// AWS.config.credentials. That's what this function does.
async function getAwsCredentials(
  accessToken: string | null | undefined
): Promise<Credentials> {
  if (isTesting()) {
    return {
      accessKeyId: "S3RVER",
      secretAccessKey: "S3RVER",
      identityId: "",
      expiration: new Date(8640000000000000),
    };
  }

  if (cachedCredentials) {
    if (
      cachedCredentials &&
      cachedCredentials.expiration &&
      Date.now() < cachedCredentials.expiration.getTime() - 60000 &&
      lastAccessToken === accessToken
    ) {
      return cachedCredentials;
    }
  }

  if (!accessToken) {
    throw Error("accessToken is empty.");
  }

  lastAccessToken = accessToken;

  // This is needed for some reason. Will get this error without it:
  //   ConfigError: Missing region in config
  const authenticator = `cognito-idp.${config.cognito.REGION}.amazonaws.com/${config.cognito.USER_POOL_ID}`;

  const client = new CognitoIdentityClient({
    region: config.cognito.REGION,
  });

  const identityParams = {
    IdentityPoolId: config.cognito.IDENTITY_POOL_ID,
    Logins: {
      [authenticator]: accessToken,
    },
  };

  // Get the identity ID
  const identityIdResponse = await client.send(
    new GetIdCommand(identityParams)
  );
  const identityId = identityIdResponse.IdentityId;

  if (!identityId) {
    throw new Error("Failed to get identity ID");
  }

  // Get the AWS credentials
  const credentialsResponse = await client.send(
    new GetCredentialsForIdentityCommand({
      IdentityId: identityId,
      Logins: {
        [authenticator]: accessToken,
      },
    })
  );
  const { Credentials, IdentityId } = credentialsResponse;

  if (!Credentials) {
    throw new Error("Failed to get AWS credentials");
  }

  const cognitoCredentials: Credentials = {
    accessKeyId: Credentials.AccessKeyId!,
    secretAccessKey: Credentials.SecretKey!,
    expiration: Credentials.Expiration!,
    sessionToken: Credentials.SessionToken!,
    identityId: IdentityId!,
  };

  cachedCredentials = cognitoCredentials;

  return cachedCredentials;
}
